import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	NgZone,
	OnDestroy,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { ObjectDetector, ObjectDetectorResult } from '@mediapipe/tasks-vision';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ScanComponent, SpinnerComponent } from '@tecalisdev/infinity';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { CameraService } from 'src/app/shared/services/camera.service';
import { IBarcodeCapturedFrame, IDocumentDetection } from '../../../models/ocr';
import { MODEL_DOCUMENT_CATEGORY } from '../../../models/user-identification';
import { PositionDocumentService } from '../../../services/ocr/position-document.service';

/** Mime type lossless para franja/frame barcode (PDF417). */
const CROP_IMAGE_MIME = 'image/png';
/** Quiet zone blanca alineada con ms-python-image-utils (`crop_barcode_colombian._add_quiet_zone`). */
const QUIET_ZONE_FRAC = 0.08;
const QUIET_ZONE_MIN_PX = 12;
const CAMERA_PERMISSION_ERROR_I18N_KEY = 'OCR.BARCODE.CAMERA_PERMISSION_ERROR';

@Component({
	selector: 'app-barcode-scan',
	standalone: true,
	imports: [TranslatePipe, ScanComponent, SpinnerComponent, VideoComponent],
	templateUrl: './barcode-scan.component.html',
	styleUrls: ['./barcode-scan.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class BarcodeScanComponent implements OnDestroy {
	@Input({ required: true }) constraints!: MediaStreamConstraints;
	@Input() documentDetector: ObjectDetector | null = null;
	@Input() instructionI18nKey = 'OCR.BARCODE.INSTRUCTION';
	/**
	 * Si true, la captura **manual** recorta la franja del overlay + quiet zone.
	 * Auto siempre envía el frame completo (MediaPipe no garantiza PDF417 en zona).
	 */
	@Input() cropStripOnManual = true;

	@Output() imageCaptured = new EventEmitter<IBarcodeCapturedFrame>();
	@Output() cameraPermissionRevoked = new EventEmitter<void>();

	@ViewChild('canvasEl', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

	public cameraReady = false;
	public scanning = false;
	public errorMsg = '';
	public videoRemountVersion = 0;

	private stream: MediaStream | null = null;
	private scannerVideo: HTMLVideoElement | null = null;
	private animFrameId: number | null = null;
	private runningMode: 'IMAGE' | 'VIDEO' = 'IMAGE';
	private lastVideoTime = -1;
	private results: ObjectDetectorResult | undefined;
	private capturePaused = false;
	private lastCaptureMs = 0;
	private readonly captureCooldownMs = 800;

	constructor(
		private ngZone: NgZone,
		private cameraService: CameraService,
		private positionDocumentService: PositionDocumentService,
		private trans: TranslateService,
	) {}

	ngOnDestroy(): void {
		this.stopCamera();
	}

	public restart(): void {
		this.capturePaused = false;
		this.errorMsg = '';
		this.stopCamera();
		this.videoRemountVersion++;
	}

	public resumeCapture(): void {
		this.capturePaused = false;
	}

	/** Captura manual (sin comprobación de geometría MediaPipe); la automática sigue en el bucle RAF. */
	public captureManually(): void {
		if (!this.cameraReady || this.capturePaused) {
			return;
		}

		const now = performance.now();
		if (now - this.lastCaptureMs < this.captureCooldownMs) {
			return;
		}

		const video = this.scannerVideo;
		const canvas = this.canvasRef?.nativeElement;
		if (!video || !canvas || !this.isVideoFrameReady(video)) {
			return;
		}

		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}

		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		this.positionDocumentService.setBarcodeStripRect(canvas, this.getViewportAspect(video));
		this.emitCapturedFrame(canvas, now, 'manual');
	}

	onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.stream = camera.stream;
		this.scannerVideo = camera.video;
		this.cameraReady = true;
		this.scanning = true;
		this.tick();
	}

	onCameraError(_err: unknown): void {
		this.errorMsg = this.trans.instant(CAMERA_PERMISSION_ERROR_I18N_KEY);
		this.cameraReady = false;
		this.scanning = false;
	}

	public onCameraPermissionRevoked(): void {
		this.stopCamera();
		this.errorMsg = this.trans.instant(CAMERA_PERMISSION_ERROR_I18N_KEY);
		this.cameraPermissionRevoked.emit();
	}

	private tick = (): void => {
		if (this.capturePaused) {
			this.animFrameId = requestAnimationFrame(this.tick);
			return;
		}

		const video = this.scannerVideo;
		const canvas = this.canvasRef?.nativeElement;
		if (!video || !canvas) {
			this.animFrameId = requestAnimationFrame(this.tick);
			return;
		}

		if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth && video.videoHeight) {
			void this.processFrame(video, canvas);
		} else {
			this.animFrameId = requestAnimationFrame(this.tick);
		}
	};

	private async processFrame(video: HTMLVideoElement, canvas: HTMLCanvasElement): Promise<void> {
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			this.animFrameId = requestAnimationFrame(this.tick);
			return;
		}

		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		await this.detectDocument(video, canvas);
		this.tryCapture(canvas);
		this.animFrameId = requestAnimationFrame(this.tick);
	}

	private async detectDocument(video: HTMLVideoElement, canvas: HTMLCanvasElement): Promise<void> {
		if (!this.documentDetector) {
			return;
		}

		const viewportAspect = this.getViewportAspect(video);
		this.positionDocumentService.setBarcodeStripRect(canvas, viewportAspect);

		if (this.runningMode === 'IMAGE') {
			this.runningMode = 'VIDEO';
			await this.documentDetector.setOptions({ runningMode: this.runningMode });
		}

		const startTimeMs = performance.now();
		if (this.lastVideoTime !== video.currentTime) {
			this.lastVideoTime = video.currentTime;
			this.results = this.documentDetector.detectForVideo(video, startTimeMs);
		}

		if (this.results?.detections?.length) {
			const documentDetection: IDocumentDetection = this.positionDocumentService.getDocumentDetection(
				this.results.detections,
			);
			if (documentDetection) {
				this.positionDocumentService.setDocumentPoints(
					{
						x: documentDetection.boundingBox.originX,
						y: documentDetection.boundingBox.originY,
						width: documentDetection.boundingBox.width,
						height: documentDetection.boundingBox.height,
					},
					canvas,
				);
			}
		} else {
			this.results = undefined;
			this.positionDocumentService.setDocumentPoints(null);
		}
	}

	private tryCapture(canvas: HTMLCanvasElement): void {
		const now = performance.now();
		if (now - this.lastCaptureMs < this.captureCooldownMs) {
			return;
		}

		const documentDetection = this.positionDocumentService.getDocumentDetection(this.results?.detections ?? []);
		if (
			!documentDetection ||
			!this.positionDocumentService.isBarcodeCaptureReady(documentDetection.name as MODEL_DOCUMENT_CATEGORY)
		) {
			return;
		}

		this.emitCapturedFrame(canvas, now, 'auto');
	}

	private emitCapturedFrame(
		canvas: HTMLCanvasElement,
		capturedAtMs: number,
		source: 'auto' | 'manual',
	): void {
		this.lastCaptureMs = capturedAtMs;
		this.capturePaused = true;

		const useStrip = source === 'manual' && this.cropStripOnManual;
		const frameBase64 = useStrip
			? this.cropBarcodeStripToPng(canvas)
			: (canvas.toDataURL(CROP_IMAGE_MIME).split(',')[1] ?? '');

		const payload: IBarcodeCapturedFrame = {
			image: frameBase64,
			cropped: useStrip,
		};
		this.ngZone.run(() => this.imageCaptured.emit(payload));
	}

	/**
	 * Recorta la franja barcode (alto relativo del overlay; ancho = canvas completo) a PNG,
	 * con quiet zone blanca (mismo criterio que Python `_add_quiet_zone`) para mejorar el decode con skip_crop.
	 */
	private cropBarcodeStripToPng(source: HTMLCanvasElement): string {
		let strip = this.positionDocumentService.getBarcodeStripRect();
		if (!strip) {
			const video = this.scannerVideo;
			const aspect = video ? this.getViewportAspect(video) : source.width / Math.max(source.height, 1);
			strip = this.positionDocumentService.setBarcodeStripRect(source, aspect);
		}

		const srcW = source.width;
		const srcH = source.height;
		const x = 0;
		const y = Math.max(0, Math.min(Math.floor(strip.y), srcH - 1));
		const width = srcW;
		const height = Math.max(1, Math.min(Math.floor(strip.height), srcH - y));
		const pad = Math.max(QUIET_ZONE_MIN_PX, Math.floor(Math.min(width, height) * QUIET_ZONE_FRAC));

		const cropCanvas = document.createElement('canvas');
		cropCanvas.width = width + pad * 2;
		cropCanvas.height = height + pad * 2;
		const ctx = cropCanvas.getContext('2d');
		if (!ctx) {
			return source.toDataURL(CROP_IMAGE_MIME).split(',')[1] ?? '';
		}

		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, cropCanvas.width, cropCanvas.height);
		ctx.drawImage(source, x, y, width, height, pad, pad, width, height);
		return cropCanvas.toDataURL(CROP_IMAGE_MIME).split(',')[1] ?? '';
	}

	private isVideoFrameReady(video: HTMLVideoElement): boolean {
		return video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0 && video.videoHeight > 0;
	}

	private getViewportAspect(video: HTMLVideoElement): number {
		const rect = video.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) {
			return rect.width / rect.height;
		}
		return video.videoWidth / video.videoHeight;
	}

	private stopCamera(): void {
		if (this.animFrameId !== null) {
			cancelAnimationFrame(this.animFrameId);
			this.animFrameId = null;
		}
		this.stream?.getTracks().forEach((t) => t.stop());
		this.stream = null;
		this.scannerVideo = null;
		this.cameraService.stream = null;
		this.scanning = false;
		this.cameraReady = false;
		this.runningMode = 'IMAGE';
		this.lastVideoTime = -1;
		this.results = undefined;
	}
}
